import { recursiveToString, SortInput } from '@apeiron/common'
import { StarFilterInput, StarSortBy } from '@apeiron/star'
import useFilterQuery, {
  FilterQueryHook,
  FilterQueryProps
} from '@src/hooks/common/router/useFilterQuery'
import { extractArray, extractValue } from '@src/util/tools/query'
import { ParsedUrlQuery } from 'querystring'
import * as R from 'ramda'
import { useMemo } from 'react'

const useStarFilterQuery = (props?: Props): Hook => {
  const filledProps = useMemo(() => {
    const supportUrl = R.propOr(true, 'supportUrl', props) as boolean

    const toFilter = (query: ParsedUrlQuery): StarFilterInput => {
      return {
        asteroidBeltSlotsMax: extractValue<number>(
          query,
          'asteroidBeltSlotsMax'
        ),
        asteroidBeltSlotsMin: extractValue<number>(
          query,
          'asteroidBeltSlotsMin'
        ),
        cometSlotsMax: extractValue<number>(query, 'cometSlotsMax'),
        cometSlotsMin: extractValue<number>(query, 'cometSlotsMin'),
        constellationComposes: extractArray(query, 'constellationComposes'),
        coreEffect: extractArray(query, 'coreEffect'),
        galaxy: extractArray(query, 'galaxy'),
        hasCelestialObject: extractValue<boolean>(query, 'hasCelestialObject'),
        hasPermanentNova: extractValue<boolean>(query, 'hasPermanentNova'),
        kuiperBeltSlotsMax: extractValue<number>(query, 'kuiperBeltSlotsMax'),
        kuiperBeltSlotsMin: extractValue<number>(query, 'kuiperBeltSlotsMin'),
        nftListingStatus: extractArray(query, 'nftListingStatus'),
        ownerWalletAddresses: extractArray(query, 'ownerWalletAddresses'),
        starID: recursiveToString(extractArray(query, 'starID')),
        sunSlotsMax: extractValue<number>(query, 'sunSlotsMax'),
        sunSlotsMin: extractValue<number>(query, 'sunSlotsMin'),
        supernovaSlotsMax: extractValue<number>(query, 'supernovaSlotsMax'),
        supernovaSlotsMin: extractValue<number>(query, 'supernovaSlotsMin'),
        agingBuffMax: extractValue<number>(query, 'agingBuffMax'),
        agingBuffMin: extractValue<number>(query, 'agingBuffMin'),
        orbitalTrackCountMax: extractValue<number>(
          query,
          'orbitalTrackCountMax'
        ),
        orbitalTrackCountMin: extractValue<number>(
          query,
          'orbitalTrackCountMin'
        ),
        tier: extractArray(query, 'tier')
      }
    }

    const toSort = (query: ParsedUrlQuery): SortInput<StarSortBy> => {
      return {
        sortBy: R.propOr(StarSortBy.PriceAsc, 'sortBy', query)
      }
    }

    return {
      supportUrl,
      queryToFilterInput: toFilter,
      queryToSortInput: toSort,
      excludedSearchParams: ['page']
    }
  }, [props])

  const {
    filterInput,
    isReady,
    sortInput,
    setBothInput,
    setFilterInput,
    setSortInput
  } = useFilterQuery(filledProps)

  return {
    filterInput,
    isReady,
    sortInput,
    setBothInput,
    setFilterInput,
    setSortInput
  }
}

type Props = Omit<
  FilterQueryProps<StarFilterInput, StarSortBy>,
  'queryToFilterInput' | 'queryToSortInput'
>

type Hook = FilterQueryHook<StarFilterInput, StarSortBy>

export default useStarFilterQuery
